// ** React Imports
import { Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

// ** Redux Imports
import { store } from './redux/store'
import { Provider, useDispatch } from 'react-redux'
import { handleLogout } from '@store/authentication'

// ** Intl, CASL & ThemeColors Context
import ability from './configs/acl/ability'
import { AbilityContext } from './utility/context/Can'
import { ThemeContext } from './utility/context/ThemeColors'

// ** ThemeConfig
import themeConfig from './configs/themeConfig'

// ** Toast
import { Toaster, toast } from 'react-hot-toast'

// ** i18n
import './configs/i18n'

// ** Spinner (Splash Screen)
import Spinner from './@core/components/spinner/Fallback-spinner'

// ** PrismJS
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/components/prism-jsx.min'

// ** React Perfect Scrollbar
import 'react-perfect-scrollbar/dist/css/styles.css'

// ** React Hot Toast Styles
import '@styles/react/libs/react-hot-toasts/react-hot-toasts.scss'

// ** Core styles
import './@core/assets/fonts/feather/iconfont.css'
import './@core/scss/core.scss'
import './assets/scss/style.scss'

// ** Service Worker
import * as serviceWorker from './serviceWorker'

// ** Apollo Client
import { ApolloClient, InMemoryCache, ApolloProvider, gql, HttpLink, ApolloLink, Observable } from '@apollo/client'
import { onError } from '@apollo/client/link/error' // Import onError to handle errors

// ** Constants
import { apiGraphURL, getTokenApi } from './services/Constants'


// ** Lazy load app
const LazyApp = lazy(() => import('./App'))

const container = document.getElementById('root')
const root = createRoot(container)

const httpLink = new HttpLink({ uri: apiGraphURL })

const authMiddleware = () => new ApolloLink((operation, forward) => {
  let authToken = getTokenApi()
  if (authToken) {
    operation.setContext({
      headers: {
        authorization: authToken
      }
    })
  }

  return forward(operation)
  // Use the forward operation to chain other links
  // return new Observable(observer => {
  //   let sub
  //   try {
  //     sub = forward(operation).subscribe({
  //       next: response => {
  //         // Handle HTTP errors
  //         console.log("response", response)
  //         if (response.errors) {
  //           // Handle the error here, e.g., log it or perform some action
  //           console.error('GraphQL request error:', response.errors)
  //         }
  //         observer.next(response)
  //       },
  //       error: networkError => {
  //         // Handle network errors
  //         console.error('Network error:', networkError)
  //         observer.error(networkError)
  //       },
  //       complete: observer.complete.bind(observer)
  //     })
  //   } catch (error) {
  //     console.error('Network error 2:', error)
  //     observer.error(error)
  //   }

  //   // Return teardown logic
  //   return () => {
  //     if (sub) sub.unsubscribe()
  //   }
  // })
})
const checkTokenIsExpirated = () => {
  const token = JSON.parse(sessionStorage.getItem('accessToken'))

  if (token && token.split(".") && token.split(".").length === 3) {
    // Decode the payload (which is base64 encoded)
    let base64Url = token.split('.')[1]
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    let jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`
    }).join(''))

    const decoded = JSON.parse(jsonPayload)
    console.log(decoded)  // You can view the decoded token here

    if (Date.now() >= decoded.exp * 1000) {  // exp is in seconds, Date.now() gives milliseconds
      console.log('Time Expired')
      toast.error('La sesión ha caducado, por razones de seguridad vuelva a iniciar sesión')
      sessionStorage.clear()
      setTimeout(() => {
        window.location.href = '/Login'
      }, 1500)
    }
  }
}

const responseLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    checkTokenIsExpirated(response)
    return response
  })
})

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    // Handle GraphQL errors
    graphQLErrors.forEach(({ message, locations, path }) => {
      checkTokenIsExpirated()
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    })
  }
  if (networkError) {
    // Handle network errors
    checkTokenIsExpirated()
    console.error(`[Network error]: ${networkError}`)
  }
})
const client = new ApolloClient({
  link: ApolloLink.from([errorLink, responseLink, authMiddleware().concat(httpLink)]),
  cache: new InMemoryCache()
})

root.render(
  <ApolloProvider client={client}>
    <BrowserRouter>
      <Provider store={store}>
        <Suspense fallback={<Spinner />}>
          <AbilityContext.Provider value={ability}>
            <ThemeContext>
              <LazyApp />
              <Toaster position={themeConfig.layout.toastPosition} toastOptions={{ className: 'react-hot-toast' }} />
            </ThemeContext>
          </AbilityContext.Provider>
        </Suspense>
      </Provider>
    </BrowserRouter>
  </ApolloProvider>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
